import React, {
  createContext,
  Dispatch,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import {
  ViImageData,
  ViCoreProcessing,
  ViCorePostCaseResponse,
  ViImageStatus,
} from "../models";
import { parsePostImageResponse } from "../services/Utility";
import { ViCoreClient } from "../services";
import { useVi, ViAction, ViActionType, ViState } from "./ViContext";
import { useHistory } from "react-router";
import {
  ViAllImagesDeletedError,
  ViError,
  ViErrorType,
  ViNoImagesActiveError,
} from "../models/Errors";
import { History } from "history";
import { useState } from "react";
import { AppContext, useApp } from "./AppContext";
import { useAuth } from "./AuthContext";

interface ViCoreState {
  isViCore: boolean;
  status: ViCoreActionType | "READY";
  isLoading: boolean;
  caseId?: Promise<string> | string;
  activeImageCount: number;
  adding?: boolean;
  imagesToUpload?: ViImageData[];
}

interface ViCoreContext extends ViCoreState {
  dispatch: Dispatch<ViCoreAction>;
}

enum ViCoreActionType {
  CreateCase = "CREATE CASE",
  CreateCaseRequested = "CREATE CASE - REQUESTED",
  CreateCaseSuccess = "CREATE CASE - SUCCESS",
  PostImages = "POST IMAGES",
  PostImagesSuccess = "POST IMAGES - SUCCESS",
  PostImage = "POST IMAGE",
  PostImageRequested = "POST IMAGE - REQUESTED",
  PostImageSuccess = "POST IMAGE - SUCCESS",
  DeleteImage = "DELETE IMAGE",
  DeleteImageRequested = "DELETE IMAGE - REQUESTED",
  DeleteImageSuccess = "DELETE IMAGE - SUCCESS",
  GetProcessing = "GET PROCESSING",
  GetProcessingRequested = "GET PROCESSING - REQUESTED",
  GetProcessingSuccess = "GET PROCESSING - SUCCESS",
  InitFromStorage = "INIT FROM STORAGE",
  Error = "ERROR",
}

type ViCoreAction =
  | { type: ViCoreActionType.CreateCase }
  | { type: ViCoreActionType.CreateCaseRequested; payload: { caseId: Promise<string> } }
  | { type: ViCoreActionType.CreateCaseSuccess }
  | { type: ViCoreActionType.PostImages }
  | { type: ViCoreActionType.PostImage; payload: { image: ViImageData } }
  | { type: ViCoreActionType.PostImageRequested; payload: { imageId: string } }
  | {
      type: ViCoreActionType.PostImageSuccess;
      payload: { image: ViImageData };
    }
  | { type: ViCoreActionType.PostImagesSuccess }
  | { type: ViCoreActionType.DeleteImage; payload: { deletedImage: ViImageData } }
  | { type: ViCoreActionType.DeleteImageRequested }
  | { type: ViCoreActionType.DeleteImageSuccess }
  | { type: ViCoreActionType.GetProcessing }
  | { type: ViCoreActionType.GetProcessingRequested }
  | {
      type: ViCoreActionType.GetProcessingSuccess;
      payload: { processingResponse: ViCoreProcessing };
    }
  | {
      type: ViCoreActionType.InitFromStorage;
      payload: { caseId: string; viState: ViState };
    }
  | {
      type: ViCoreActionType.Error;
      payload: { error: ViError };
    };

const initialState = {
  isViCore: true,
  status: "READY" as const,
  isLoading: false,
  activeImageCount: 0,
};

const ViCoreSubContext = createContext<ViCoreContext | undefined>(undefined);
export const useViCore = () => {
  const context = useContext(ViCoreSubContext);
  if (context === undefined)
    throw new ViError("useViCore must be used within a ViCoreSubContext Provider");
  return context;
};

export const ViCoreProvider = React.memo(({ children }: { children: ReactElement }) => {
  const App = useApp();
  const history = useHistory();
  const AuthContext = useAuth();
  const { actions: viActions, dispatch: viDispatch } = useVi();
  const [actions, setActions] = useState<ViCoreAction[]>([]);
  const [state, rawDispatch] = useReducer(reducer, initialState);
  const viCoreClient = useMemo(() => ViCoreClient.init(AuthContext), [AuthContext]);

  const dispatch = useCallback((action: ViCoreAction) => {
    rawDispatch(action);
    setActions((actions) => [...actions, action]);
  }, []);

  useEffect(() => {
    for (const action of actions) {
      sideEffects(state, action, {
        dispatch,
        viDispatch,
        history,
        viCoreClient,
        App,
      });
    }
    if (actions.length) setActions([]);
  }, [actions, history, dispatch, viDispatch, state, App, viCoreClient]);

  useEffect(() => {
    for (const action of viActions) {
      rawDispatch(action);
      viSideEffects(state, action, { history, dispatch, viDispatch });
    }
  }, [viActions, state, history, dispatch, viDispatch]);

  return (
    <ViCoreSubContext.Provider value={{ ...state, dispatch }}>
      {children}
    </ViCoreSubContext.Provider>
  );
});

const viSideEffects = async (
  state: ViCoreState,
  action: ViAction,
  {
    history,
    dispatch,
    viDispatch,
  }: {
    history: History;
    dispatch: Dispatch<ViCoreAction>;
    viDispatch: Dispatch<ViAction>;
  }
) => {
  switch (action.type) {
    case ViActionType.ImagesAdded:
      const { adding } = action.payload;
      if (!adding) dispatch({ type: ViCoreActionType.CreateCase });
      break;

    case ViActionType.ImageLoaded:
      const { image } = action.payload;
      dispatch({ type: ViCoreActionType.PostImage, payload: { image } });
      break;

    case ViActionType.ImageDeleted: {
      const { deletedImage } = action.payload;
      dispatch({ type: ViCoreActionType.DeleteImage, payload: { deletedImage } });
      break;
    }

    case ViActionType.LoadResultsFromStorageSuccess: {
      const { caseId, viState } = action.payload;
      dispatch({ type: ViCoreActionType.InitFromStorage, payload: { caseId, viState } });
      break;
    }
  }
};

const sideEffects = async (
  state: ViCoreState,
  action: ViCoreAction,
  {
    history,
    dispatch,
    viDispatch,
    viCoreClient,
    App,
  }: {
    history: History;
    dispatch: Dispatch<ViCoreAction>;
    viDispatch: Dispatch<ViAction>;
    viCoreClient: typeof ViCoreClient;
    App: AppContext;
  }
): Promise<boolean> => {
  try {
    const ViCoreClient = await viCoreClient;
    switch (action.type) {
      case ViCoreActionType.CreateCase: {
        const createCaseResponse = ViCoreClient.createCase();
        dispatch({
          type: ViCoreActionType.CreateCaseRequested,
          payload: { caseId: getCaseId(createCaseResponse) },
        });
        dispatch({ type: ViCoreActionType.CreateCaseSuccess });
        break;
      }

      case ViCoreActionType.PostImage: {
        if (state.status !== ViCoreActionType.PostImages)
          dispatch({ type: ViCoreActionType.PostImages });
        const caseId = await state.caseId;
        const { image } = action.payload;
        if (caseId) {
          const uploadImagePromise = ViCoreClient.uploadImages(caseId, [image]);
          dispatch({
            type: ViCoreActionType.PostImageRequested,
            payload: { imageId: image.id },
          });
          const uploadImageResponse = await uploadImagePromise;
          const parsedImageData = parsePostImageResponse(image, uploadImageResponse);
          dispatch({
            type: ViCoreActionType.PostImageSuccess,
            payload: { image: parsedImageData },
          });
          viDispatch({
            type: ViActionType.ImageUploaded,
            payload: { image: parsedImageData },
          });
        }
        break;
      }

      case ViCoreActionType.PostImageSuccess: {
        const { imagesToUpload } = state;
        if (imagesToUpload?.length === 0)
          dispatch({ type: ViCoreActionType.PostImagesSuccess });
        break;
      }

      case ViCoreActionType.PostImagesSuccess: {
        const { activeImageCount } = state;
        if (activeImageCount > 0) dispatch({ type: ViCoreActionType.GetProcessing });
        else throw new ViNoImagesActiveError();
        break;
      }

      case ViCoreActionType.GetProcessing: {
        const caseId = await state.caseId;
        if (caseId) {
          const t0 = performance.now();
          const getProcessingPromise = ViCoreClient.getProcessing(caseId, 0);
          dispatch({ type: ViCoreActionType.GetProcessingRequested });
          const getProcessingResponse = await getProcessingPromise;
          const t1 = performance.now();
          const timing = Math.round((t1 - t0) / 100) / 10;
          const {
            id,
            inferenceFlow: { images, results },
          } = getProcessingResponse;
          if (state.adding)
            history.push(`/vi/cases/${caseId}/processings/${getProcessingResponse.id}`);
          else
            history.replace(
              `/vi/cases/${caseId}/processings/${getProcessingResponse.id}`
            );
          const payload = {
            processingResponse: {
              id,
              ...results,
              images,
              timing,
            },
            caseId: await state.caseId,
            processingId: getProcessingResponse.id,
          };
          dispatch({
            type: ViCoreActionType.GetProcessingSuccess,
            payload,
          });
          viDispatch({
            type: ViActionType.ViResultsReceived,
            payload,
          });
        }
        break;
      }

      case ViCoreActionType.DeleteImage: {
        const caseId = await state.caseId;
        if (!caseId) break;
        const { id } = action.payload.deletedImage;
        const deleteImagePromise = ViCoreClient.deleteImage(caseId, id);
        dispatch({ type: ViCoreActionType.DeleteImageRequested });
        await deleteImagePromise;
        dispatch({ type: ViCoreActionType.DeleteImageSuccess });
        break;
      }

      case ViCoreActionType.DeleteImageSuccess: {
        const { activeImageCount } = state;
        if (activeImageCount > 0) dispatch({ type: ViCoreActionType.GetProcessing });
        else throw new ViAllImagesDeletedError();
        break;
      }
    }
    return true;
  } catch (e) {
    const error = new ViError(e);
    error.actionType = action.type;
    App.handleError && App.handleError(error);
    if (error.type !== ViErrorType.Warning && error.type !== ViErrorType.Silent) {
      dispatch({
        type: ViCoreActionType.Error,
        payload: { error },
      });
    }
    viDispatch({ type: ViActionType.Error, payload: { error } });
    return false;
  }
};

const reducer = (state: ViCoreState, action: ViAction | ViCoreAction): ViCoreState => {
  switch (action.type) {
    case ViActionType.ImagesAdded: {
      const { adding, imagesToUpload } = action.payload;
      return { ...(adding ? state : initialState), adding, imagesToUpload };
    }

    case ViCoreActionType.CreateCase: {
      return { ...state, status: action.type, isLoading: true };
    }

    case ViCoreActionType.CreateCaseSuccess:
    case ViCoreActionType.GetProcessing:
    case ViCoreActionType.GetProcessingRequested:
    case ViCoreActionType.DeleteImageRequested:
    case ViCoreActionType.DeleteImageSuccess:
    case ViCoreActionType.PostImages:
    case ViCoreActionType.PostImagesSuccess: {
      return { ...state, status: action.type };
    }

    case ViCoreActionType.CreateCaseRequested: {
      const { caseId } = action.payload;
      return { ...state, status: action.type, caseId };
    }
    case ViCoreActionType.DeleteImage: {
      return {
        ...state,
        status: action.type,
        activeImageCount: state.activeImageCount - 1,
      };
    }

    case ViCoreActionType.PostImage: {
      return { ...state, status: ViCoreActionType.PostImages };
    }

    case ViCoreActionType.PostImageSuccess: {
      const { error, localId } = action.payload.image;
      const activeImageCount = state.activeImageCount + (error ? 0 : 1);
      const imagesToUpload = state.imagesToUpload?.filter(
        (img) => img.localId !== localId
      );
      return { ...state, activeImageCount, imagesToUpload };
    }

    case ViCoreActionType.GetProcessingSuccess: {
      return {
        ...state,
        status: action.type,
        isLoading: false,
      };
    }

    case ViCoreActionType.InitFromStorage: {
      const {
        caseId,
        viState: { images },
      } = action.payload;
      const activeImageCount =
        images?.filter((img) => img.status === ViImageStatus.ACTIVE).length || 0;
      return { ...state, caseId, activeImageCount };
    }

    case ViCoreActionType.Error: {
      return initialState;
    }

    default:
      return state;
  }
};

const getCaseId = async (createCaseResonse: Promise<ViCorePostCaseResponse>) =>
  (await createCaseResonse).id;
